// NotasSolicitud.jsx — Permite a creador y aprobadores dejar notas en una
// solicitud. Cada nueva nota dispara notificación in-app + email vía el
// helper del backend. El autor puede editar/eliminar sus propias notas tipo
// 'manual'. Las notas 'cierre'/'cancelacion' (generadas por el sistema al
// aprobar/rechazar) son inmutables.

import React, { useState } from 'react';
import { Button, Form, Spinner, Badge } from 'react-bootstrap';
import moment from 'moment';
import Swal from 'sweetalert2';
import {
    agregarNotaRegistro,
    editarNotaRegistro,
    eliminarNotaRegistro,
} from '../../api/common';

const BADGE_POR_TIPO = {
    manual:      { bg: 'secondary', label: 'Nota' },
    cierre:      { bg: 'success',   label: 'Cierre' },
    cancelacion: { bg: 'danger',    label: 'Cancelación' },
};

/**
 * Comprueba si el usuario actual es creador o aprobador asignado — es la
 * misma regla del backend (`_puedeComentarRegistro`) replicada en frontend
 * para mostrar/ocultar el formulario de nueva nota.
 */
const puedeComentar = (solicitud, usuarioActual) => {
    if (!solicitud || !usuarioActual) return false;
    const uid = String(usuarioActual._id);
    const solicitanteId = String(solicitud.solicitante?._id || solicitud.solicitante);
    if (solicitanteId === uid) return true;
    for (const r of (solicitud.responsablesAprobacion || [])) {
        const rid = String(r?.usuario?._id || r?.usuario);
        if (rid === uid) return true;
    }
    return false;
};

const NotasSolicitud = ({ solicitud, usuarioActual, onActualizar }) => {
    const [texto, setTexto] = useState('');
    const [loading, setLoading] = useState(false);
    const [editandoId, setEditandoId] = useState(null);
    const [textoEditando, setTextoEditando] = useState('');

    const notas = Array.isArray(solicitud?.notas) ? solicitud.notas : [];
    const puedeAgregar = puedeComentar(solicitud, usuarioActual);

    const handleAgregar = async (e) => {
        e.preventDefault();
        const t = texto.trim();
        if (!t) return;
        setLoading(true);
        try {
            await agregarNotaRegistro(solicitud._id, t);
            setTexto('');
            onActualizar?.();
        } catch (err) {
            Swal.fire({
                icon: 'error',
                title: 'No se pudo agregar la nota',
                text: err?.response?.data?.message || err.message,
            });
        } finally {
            setLoading(false);
        }
    };

    const handleEditar = (nota) => {
        setEditandoId(String(nota._id));
        setTextoEditando(nota.descripcion);
    };

    const handleGuardarEdicion = async (notaId) => {
        const t = textoEditando.trim();
        if (!t) return;
        setLoading(true);
        try {
            await editarNotaRegistro(solicitud._id, notaId, t);
            setEditandoId(null);
            setTextoEditando('');
            onActualizar?.();
        } catch (err) {
            Swal.fire({
                icon: 'error',
                title: 'No se pudo editar la nota',
                text: err?.response?.data?.message || err.message,
            });
        } finally {
            setLoading(false);
        }
    };

    const handleEliminar = async (notaId) => {
        const r = await Swal.fire({
            title: '¿Eliminar nota?',
            text: 'Esta acción no se puede deshacer.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#dc3545',
        });
        if (!r.isConfirmed) return;

        setLoading(true);
        try {
            await eliminarNotaRegistro(solicitud._id, notaId);
            onActualizar?.();
        } catch (err) {
            Swal.fire({
                icon: 'error',
                title: 'No se pudo eliminar la nota',
                text: err?.response?.data?.message || err.message,
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="d-flex flex-column gap-3">
            {/* Formulario para agregar nota (solo si tiene permiso) */}
            {puedeAgregar && (
                <Form onSubmit={handleAgregar}>
                    <Form.Group>
                        <Form.Label className="fw-bold">
                            <i className="bi bi-chat-left-text me-2" />
                            Nueva nota
                        </Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            maxLength={2000}
                            value={texto}
                            onChange={(e) => setTexto(e.target.value)}
                            placeholder="Escribe una nota para los demás involucrados…"
                            disabled={loading}
                        />
                        <div className="d-flex justify-content-between align-items-center mt-2">
                            <small className="text-muted">{texto.length}/2000</small>
                            <Button type="submit" size="sm" variant="primary" disabled={loading || !texto.trim()}>
                                {loading ? <><Spinner size="sm" animation="border" className="me-2" />Enviando…</> : <>Agregar nota</>}
                            </Button>
                        </div>
                    </Form.Group>
                </Form>
            )}

            {/* Timeline de notas */}
            {notas.length === 0 ? (
                <div className="text-center text-muted py-4">
                    <i className="bi bi-chat-left-dots fs-1 d-block mb-2" />
                    No hay notas todavía.
                </div>
            ) : (
                <ul className="list-group list-group-flush">
                    {notas.slice().reverse().map((n) => {
                        const esAutor = String(n.usuarioId?._id || n.usuarioId) === String(usuarioActual?._id);
                        const esManual = n.tipo === 'manual' || !n.tipo;
                        const badge = BADGE_POR_TIPO[n.tipo || 'manual'] || BADGE_POR_TIPO.manual;
                        const estaEditando = editandoId === String(n._id);

                        return (
                            <li key={String(n._id)} className="list-group-item">
                                <div className="d-flex justify-content-between align-items-start mb-1">
                                    <div className="d-flex align-items-center gap-2">
                                        <strong>{n.usuarioNombre || 'Usuario'}</strong>
                                        <Badge bg={badge.bg}>{badge.label}</Badge>
                                        {n.editada && <small className="text-muted">(editada)</small>}
                                    </div>
                                    <small className="text-muted">{moment(n.fecha).format('DD/MM/YYYY HH:mm')}</small>
                                </div>

                                {estaEditando ? (
                                    <>
                                        <Form.Control
                                            as="textarea"
                                            rows={3}
                                            maxLength={2000}
                                            value={textoEditando}
                                            onChange={(e) => setTextoEditando(e.target.value)}
                                            disabled={loading}
                                        />
                                        <div className="d-flex justify-content-end gap-2 mt-2">
                                            <Button size="sm" variant="secondary" onClick={() => { setEditandoId(null); setTextoEditando(''); }} disabled={loading}>
                                                Cancelar
                                            </Button>
                                            <Button size="sm" variant="primary" onClick={() => handleGuardarEdicion(n._id)} disabled={loading || !textoEditando.trim()}>
                                                Guardar
                                            </Button>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div style={{ whiteSpace: 'pre-wrap' }}>{n.descripcion}</div>
                                        {esAutor && esManual && (
                                            <div className="d-flex justify-content-end gap-2 mt-2">
                                                <Button size="sm" variant="outline-secondary" onClick={() => handleEditar(n)} disabled={loading}>
                                                    <i className="bi bi-pencil me-1" />Editar
                                                </Button>
                                                <Button size="sm" variant="outline-danger" onClick={() => handleEliminar(n._id)} disabled={loading}>
                                                    <i className="bi bi-trash me-1" />Eliminar
                                                </Button>
                                            </div>
                                        )}
                                    </>
                                )}
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
};

export default NotasSolicitud;
